import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { meetingAPI, userAPI } from '../Services/apiService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronsUpDown, ChevronDown, ChevronsUp, AlertCircle, BookUpIcon, Trash, Download, Loader } from 'lucide-react';

import {
  Calendar as CalendarIcon,
  Search,
  Edit,
  Trash2,
  Users,
  Clock,
  MoreHorizontal,
  CheckSquare,
  AlertTriangle,
  MapPin,
  Link,
  Plus,
  RefreshCw,
  FileText,
  Eye
} from 'lucide-react';

import { Calendar } from './ui/calendar';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { format, isAfter, isBefore } from 'date-fns';
import { toast } from 'sonner';
import CreateMeetingModal from './CreateMeetingModal';
import EditMeetingModal from './EditMeetingModal';
import MOMModal from './MOMModal';
import ActionPointsViewModal from './ActionPointsViewModal';
import { showToast } from '../utils/toast';

import { handleAPIError } from '../utils/errorHandler';
import Pagination from './Pagination';
import useDebouncedValue from '../hooks/useDebouncedValue';

import ResponsiveTooltip from './ui/ResponsiveTooltip';
import CancelRecurringMeetingDialog from './CancelRecurringMeetingDialog';


const MeetingManagement = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [filterStatus, setFilterStatus] = useState('upcoming');
  const [filterOrganizer, setFilterOrganizer] = useState('all');
  const [organizerOpenDesktop, setOrganizerOpenDesktop] = useState(false);
  const [organizerOpenMobile, setOrganizerOpenMobile] = useState(false);
  const [meetingStats, setMeetingStats] = useState(null);
  const [meetingsList, setMeetingsList] = useState([]); // Paginated meetings for display
  const [allMeetingsData, setAllMeetingsData] = useState([]); // All meetings from backend (for organizer list)
  const [allUsers, setAllUsers] = useState([]); // Organizers list (for filter dropdown)
  const [allParticipants, setAllParticipants] = useState([]); // All users (for edit/create meeting modal)

  // Separate loading states for progressive UI rendering
  const [loadingMeetings, setLoadingMeetings] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingOrganizers, setLoadingOrganizers] = useState(true);
  const [isCreateMeetingModalOpen, setIsCreateMeetingModalOpen] = useState(false);
  const [isEditMeetingModalOpen, setIsEditMeetingModalOpen] = useState(false);
  const [isMOMModalOpen, setIsMOMModalOpen] = useState(false);
  const [isActionPointsViewModalOpen, setIsActionPointsViewModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [isRecurringCancelDialogOpen, setIsRecurringCancelDialogOpen] = useState(false);
  const [meetingToCancel, setMeetingToCancel] = useState(null);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({ from: null, to: null });
  const [loadingExport, setLoadingExport] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(''); // Default to backend sorting (status priority)
  const [sortDirection, setSortDirection] = useState('asc'); // desc = newest first

  const isInitialLoad = useRef(true);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      // Active column - show current sort direction with highlighted color
      return (
        <span className="text-gray-600 text-xs ml-1">
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      );
    }
    // Inactive columns - always show default ▼ (lighter color)
    return (
      <span className="text-gray-400 text-xs ml-1">
        ▼
      </span>
    );
  };

  // Truncate text with ellipsis
  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Strip HTML tags to get plain text (for truncation and length checks)
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Format status to Title Case
  const formatStatus = (status) => {
    if (status === 'completed') return 'Completed';
    if (status === 'approved') return 'Completed'; // Backward compatibility
    if (!status) return '';
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      const params = {};
      if (exportDateRange?.from) params.start_date = format(exportDateRange.from, 'yyyy-MM-dd');
      if (exportDateRange?.to) params.end_date = format(exportDateRange.to, 'yyyy-MM-dd');

      const response = await userAPI.exportmeeting(params);

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Meetings_Export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Meetings exported successfully');
    } catch (error) {
      handleAPIError(error, 'Failed to export meetings');
    } finally {
      setLoadingExport(false);
    }
  };

  // Load meetings, stats, and users - extracted as useCallback so it can be called from cancel functions
  const loadData = useCallback(async () => {
    if (!currentUser?.token) return;

    try {
      // Set loading states for each section
      if (isInitialLoad.current) {
        setLoadingMeetings(true);
        setLoadingStats(true);
        setLoadingOrganizers(true);
        isInitialLoad.current = false;
      } else {
        // On filter/pagination changes, only reload meetings
        setLoadingMeetings(true);
      }

      // Fetch all data in parallel for better performance
      const [meetingsResponse, statsResponse, organizersResponse] = await Promise.all([
        meetingAPI.getMeetings({
          search: debouncedSearch || undefined,
          state: filterStatus !== 'all' ? filterStatus : undefined,
          organizer_id: filterOrganizer !== 'all' ? parseInt(filterOrganizer) : undefined,
          include_cancelled: true,
          sort_by: sortColumn === 'title' ? 'title'
            : sortColumn === 'agenda' ? 'agenda'
              : sortColumn === 'date' ? 'scheduled_date'
                : undefined,
          sort_order: sortDirection,
          page: page,
          page_size: pageSize
        }).then(response => {
          // Process meetings response immediately
          const responseData = response.data;
          let paginatedItems, total, pages;

          if (Array.isArray(responseData)) {
            const allMeetings = responseData;
            setAllMeetingsData(allMeetings);
            total = allMeetings.length;
            pages = Math.max(1, Math.ceil(total / pageSize));
            const currentPage = Math.min(Math.max(1, page), pages);
            const startIdx = (currentPage - 1) * pageSize;
            const endIdx = startIdx + pageSize;
            paginatedItems = allMeetings.slice(startIdx, endIdx);
          } else {
            paginatedItems = responseData.items || [];
            total = responseData.total || 0;
            pages = responseData.pages || 1;
            setAllMeetingsData(paginatedItems);
          }

          setMeetingsList(paginatedItems);
          setTotal(total);
          setPages(pages);
          setHasNext(page < pages);
          setHasPrev(page > 1);
          setLoadingMeetings(false);

          return response;
        }).catch(error => {
          setLoadingMeetings(false);
          throw error;
        }),

        meetingAPI.getMeetingStats().then(response => {
          setMeetingStats(response.data || null);
          setLoadingStats(false);
          return response;
        }).catch(error => {
          setLoadingStats(false);
          throw error;
        }),

        userAPI.getOrganizers().then(response => {
          setAllUsers(response.data || []);
          setLoadingOrganizers(false);
          return response;
        }).catch(error => {
          setLoadingOrganizers(false);
          throw error;
        })

      ]);

    } catch (error) {
      handleAPIError(error, 'Failed to load data', true);
    }
  }, [currentUser?.token, page, pageSize, debouncedSearch, filterStatus, filterOrganizer, sortColumn, sortDirection]);

  // Load full list of all users (with vendors) for the EditMeetingModal participant picker.
  // This is separate from the organizer dropdown filter which uses getOrganizers().
  useEffect(() => {
    if (!currentUser?.token) return;

    const loadAllParticipants = async () => {
      try {
        let aggregated = [];
        let pg = 1;
        let hasMore = true;

        while (hasMore) {
          const resp = await userAPI.getUsers({ include_vendors: true, page: pg, page_size: 200 });
          const data = resp?.data;
          if (Array.isArray(data)) {
            aggregated = data;
            hasMore = false;
          } else if (data && data.items) {
            aggregated = [...aggregated, ...data.items];
            hasMore = data.has_next || false;
            pg++;
          } else {
            hasMore = false;
          }
        }

        setAllParticipants(aggregated);
      } catch (error) {
        console.error('Failed to fetch all participants:', error);
      }
    };

    loadAllParticipants();
  }, [currentUser?.token]);

  useEffect(() => {
    loadData();

    // Listen for meeting creation events from Layout
    const handleMeetingCreated = () => {
      loadData();
    };

    // Listen for task status changes to update action point counts
    const handleTaskStatusChanged = () => {
      loadData();
    };

    // Listen for user status changes to refresh user list
    const handleUserStatusChanged = () => {
      loadData();
    };

    window.addEventListener('meetingCreated', handleMeetingCreated);
    window.addEventListener('taskStatusChanged', handleTaskStatusChanged);
    window.addEventListener('userStatusChanged', handleUserStatusChanged);

    // Auto-refresh meetings every 60 seconds to catch status changes
    const refreshInterval = setInterval(() => {
      loadData();
    }, 60000);

    return () => {
      window.removeEventListener('meetingCreated', handleMeetingCreated);
      window.removeEventListener('taskStatusChanged', handleTaskStatusChanged);
      window.removeEventListener('userStatusChanged', handleUserStatusChanged);
      clearInterval(refreshInterval);
    };
  }, [loadData]); // Now depends on loadData


  // Organizers list from dedicated API endpoint
  // Backend already filters (employee, business_head, admin), sorts alphabetically, and formats data
  const organizers = useMemo(() => {
    // API returns [{id, name}] - ready to use, no transformation needed
    return allUsers || [];
  }, [allUsers]);

  // Use meetingsList (which is already sorted and paginated from backend)
  const paginatedMeetings = meetingsList;

  // Pagination data managed by normalized response
  const totalFiltered = total;
  const totalPages = pages;

  const handleCreateMeeting = async (meetingData, skipNotification = false) => {
    try {
      const response = await meetingAPI.createMeeting(meetingData, skipNotification);
      setMeetingsList(prev => [...prev, response.data]);

      // Update stats locally
      setMeetingStats(prev => prev ? {
        ...prev,
        total: prev.total + 1,
        upcoming: prev.upcoming + 1
      } : null);

      showToast.success('Meeting created successfully');
      setIsCreateMeetingModalOpen(false);

      // Return response so CreateMeetingModal can upload agenda documents
      return response;
    } catch (error) {
      handleAPIError(error, 'Failed to create meeting', true);
      throw error; // Re-throw to stop document upload
    }
  };

  const handleUpdateMeeting = async (meetingId, meetingData, skipNotification = false) => {
    try {
      const response = await meetingAPI.updateMeeting(meetingId, meetingData, skipNotification);
      setMeetingsList(prev => prev.map(meeting =>
        meeting.id === meetingId ? response.data : meeting
      ));

      showToast.success('Meeting updated successfully');
      setIsEditMeetingModalOpen(false);

      // Return response for agenda document upload
      return response;
    } catch (error) {
      handleAPIError(error, 'Failed to update meeting', true);
      throw error; // Re-throw to stop document upload
    }
  };

  const handleRefreshData = async () => {
    try {
      setLoadingMeetings(true);
      setLoadingStats(true);

      const [meetingsResponse, statsResponse] = await Promise.all([
        meetingAPI.getMeetings({
          search: debouncedSearch || undefined,
          state: filterStatus !== 'all' ? filterStatus : undefined,
          organizer_id: filterOrganizer !== 'all' ? parseInt(filterOrganizer) : undefined,
          include_cancelled: true,
          sort_by: sortColumn === 'title' ? 'title' : sortColumn === 'agenda' ? 'agenda' : 'scheduled_date',
          sort_order: sortDirection,
          page: page,
          page_size: pageSize
        }).then(response => {
          const responseData = response.data;
          if (Array.isArray(responseData)) {
            setMeetingsList(responseData);
            setTotal(responseData.length);
          } else {
            setMeetingsList(responseData.items || []);
            setTotal(responseData.total || 0);
            setPages(responseData.pages || 1);
            setHasNext(page < (responseData.pages || 1));
            setHasPrev(page > 1);
          }
          setLoadingMeetings(false);
          return response;
        }),
        meetingAPI.getMeetingStats().then(response => {
          setMeetingStats(response.data || null);
          setLoadingStats(false);
          return response;
        })
      ]);

      showToast.success('Meetings refreshed successfully');
    } catch (error) {
      handleAPIError(error, 'Failed to refresh meetings', true);
      setLoadingMeetings(false);
      setLoadingStats(false);
    }
  };

  const handleMOMModalOpen = (meeting) => {
    setSelectedMeeting(meeting);
    setIsMOMModalOpen(true);
  };

  const handleActionPointsViewModalOpen = (meeting) => {
    setSelectedMeeting(meeting);
    setIsActionPointsViewModalOpen(true);
  };

  const handleMOMModalSuccess = async () => {
    // Refresh meetings data to show updated action points
    try {
      const meetingsResponse = await meetingAPI.getMeetings({
        search: debouncedSearch || undefined,
        state: filterStatus !== 'all' ? filterStatus : undefined,
        organizer_id: filterOrganizer !== 'all' ? parseInt(filterOrganizer) : undefined,
        include_cancelled: true,
        sort_by: sortColumn === 'title' ? 'title' : sortColumn === 'agenda' ? 'agenda' : 'scheduled_date',
        sort_order: sortDirection,
        page: page,
        page_size: pageSize
      });

      const responseData = meetingsResponse.data;
      if (Array.isArray(responseData)) {
        setMeetingsList(responseData);
      } else {
        setMeetingsList(responseData.items || []);
        setTotal(responseData.total || 0);
        setPages(responseData.pages || 1);
      }
    } catch (error) {
      handleAPIError(error, 'Failed to refresh meetings after adding action points', true);
    }
  };

  const handleDeleteMeeting = (meetingId) => {
    const meeting = meetingsList.find(m => m.id === meetingId);
    setMeetingToDelete(meeting);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteMeeting = async () => {
    if (!meetingToDelete) return;

    try {
      await meetingAPI.deleteMeeting(meetingToDelete.id);

      // Refresh data from server
      try {
        setLoadingMeetings(true);
        setLoadingStats(true);

        const [meetingsResponse, statsResponse] = await Promise.all([
          meetingAPI.getMeetings({
            search: debouncedSearch || undefined,
            state: filterStatus !== 'all' ? filterStatus : undefined,
            organizer_id: filterOrganizer !== 'all' ? parseInt(filterOrganizer) : undefined,
            include_cancelled: true,
            sort_by: sortColumn === 'title' ? 'title' : sortColumn === 'agenda' ? 'agenda' : 'scheduled_date',
            sort_order: sortDirection,
            page: page,
            page_size: pageSize
          }),
          meetingAPI.getMeetingStats()
        ]);

        const responseData = meetingsResponse.data;
        if (Array.isArray(responseData)) {
          setMeetingsList(responseData);
          setTotal(responseData.length);
        } else {
          setMeetingsList(responseData.items || []);
          setTotal(responseData.total || 0);
          setPages(responseData.pages || 1);
        }

        setMeetingStats(statsResponse.data || null);
      } catch (refreshError) {
        handleAPIError(refreshError, 'Failed to refresh meetings after deletion', true);
      } finally {
        setLoadingMeetings(false);
        setLoadingStats(false);
      }

      showToast.success('Meeting deleted successfully');
      setDeleteConfirmOpen(false);
      setMeetingToDelete(null);
    } catch (error) {
      handleAPIError(error, 'Failed to delete meeting', true);
      setDeleteConfirmOpen(false);
      setMeetingToDelete(null);
    }
  };

  // Open cancel confirmation modal
  const handleCancelClick = (meeting) => {
    setMeetingToCancel(meeting);

    // RELIABLE: Check if meeting is part of a recurring series
    // Backend sets recurring_series_id for all instances in a series
    const isRecurring = Boolean(
      meeting.recurring_series_id ||  // PRIMARY: Most reliable indicator
      meeting.is_recurring ||          // Fallback 1: Compatibility
      (meeting.recurrence && meeting.recurrence !== 'none')  // Fallback 2: Legacy check
    );

    if (isRecurring) {
      setIsRecurringCancelDialogOpen(true);
    } else {
      setCancelConfirmOpen(true);
    }
  };

  // Confirm cancel meeting (for non-recurring meetings)
  const confirmCancelMeeting = async () => {
    if (!meetingToCancel) return;

    try {
      const response = await meetingAPI.cancelMeeting(meetingToCancel.id, { cancelAll: true });

      // Update local state to show cancelled status
      setMeetingsList(prev => prev.map(m =>
        m.id === meetingToCancel.id ? { ...m, state: 'cancelled', status: 'cancelled' } : m
      ));

      // Update in allMeetingsData too
      setAllMeetingsData(prev => prev.map(m =>
        m.id === meetingToCancel.id ? { ...m, state: 'cancelled', status: 'cancelled' } : m
      ));

      // Update stats
      setMeetingStats(prev => prev ? {
        ...prev,
        [meetingToCancel.state]: Math.max(0, (prev[meetingToCancel.state] || 0) - 1),
        cancelled: (prev.cancelled || 0) + 1
      } : null);

      showToast.success('Meeting cancelled successfully');
      setCancelConfirmOpen(false);
      setMeetingToCancel(null);

      // Reload meeting list to reflect changes
      loadData();
    } catch (error) {
      handleAPIError(error, 'Failed to cancel meeting', true);
    }
  };

  // Handle recurring meeting cancellation
  const handleRecurringCancelConfirm = async (cancel_all, occurrence_date, cancelScope) => {
    if (!meetingToCancel) return;

    try {
      setIsCancelLoading(true);

      const response = await meetingAPI.cancelMeeting(meetingToCancel.id, {
        cancel_all,
        cancel_scope: cancelScope,  // NEW: Pass the scope ('this', 'from_today', or 'from_tomorrow')
        occurrence_date,
      });

      if (response) {
        // Success toast based on scope
        const toastMessage =
          cancelScope === 'this'
            ? 'Meeting occurrence cancelled successfully'
            : cancelScope === 'from_today'
              ? `This meeting and all future occurrences cancelled`
              : 'All future occurrences cancelled (today preserved)';

        showToast.success(toastMessage);

        // Reload meeting list to reflect changes
        loadData();
      }
    } catch (error) {
      console.error('Error cancelling recurring meeting:', error);
      handleAPIError(error, 'Failed to cancel meeting', true);
    } finally {
      setIsCancelLoading(false);
      setIsRecurringCancelDialogOpen(false);
      setMeetingToCancel(null);
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'status-completed border-0 hover:!bg-green-100';
      case 'scheduled':
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-0 hover:!bg-blue-100';
      case 'ongoing': return 'bg-yellow-100 text-yellow-800 border-0 hover:!bg-yellow-100';
      case 'pending': return 'bg-amber-100 text-amber-800 border-0 hover:!bg-amber-100';
      case 'cancelled': return 'bg-red-100 text-red-800 border-0 hover:!bg-red-100';
      default: return 'bg-gray-100 text-gray-800 border-0 hover:!bg-gray-100';
    }
  };

  const statusOptions = [

    { value: 'upcoming', label: 'Upcoming' },
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <Card className="glass-card border-0">
      <div className='pt-2'></div>

      <CardContent className="space-y-3">
        {/* <div className='hidden sm:flex  gap-10'> */}
        <div className='flex flex-col  xl:flex-row gap-4 xl:gap-5 sm:flex py-4'>
          {/* Stats Cards */}
          {loadingStats ? (
            <div className="grid grid-cols-2 md:grid-cols-3 md:w-full lg:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="loading-shimmer h-24 rounded-lg"></div>
              ))}
            </div>
          ) : meetingStats && (
            <div className="grid grid-cols-2 md:grid-cols-3 md:w-full lg:grid-cols-5 gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-2 sm:p-4 rounded-lg border border-blue-200">
                <div className="flex  items-center justify-between">
                  <div>
                    <div className='flex gap-2 '>
                      <p className="text-sm font-medium text-wrap text-blue-600">Total Meetings</p>
                      <CalendarIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{meetingStats.total}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r    from-green-50 to-green-100 p-2 sm:p-4 rounded-lg border border-green-200">
                <div className="flex items-center  justify-between">
                  <div>
                    <div className='flex gap-2  items-center'>
                      <p className="text-sm font-medium    text-green-600">Upcoming</p>
                      <BookUpIcon className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-900">{meetingStats.upcoming}</p>
                  </div>

                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-2 sm:p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className='flex gap-2 items-center'>
                      <p className="text-sm font-medium text-purple-600">Ongoing</p>
                      <Clock className="w-4 h-4 text-purple-600" />
                    </div>
                    <p className="text-2xl font-bold text-purple-900">{meetingStats.ongoing || 0}</p>
                  </div>

                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-2 px-1 sm:p-4 rounded-lg border border-teal-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className='flex gap-2 items-center'>
                      <p className="text-sm font-medium text-teal-600">Completed</p>
                      <CheckSquare className="w-4 h-4 text-teal-600" />
                    </div>
                    <p className="text-2xl font-bold text-teal-900">{meetingStats.completed}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-red-100 p-2 sm:p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className='flex gap-2 items-center'>
                      <p className="text-sm font-medium text-amber-600">Pending</p>
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-red-900">{meetingStats.pending || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}



          <div>
            <div className="flex flex-col  sm:flex-row gap-4 ">

              <div className="flex flex-wrap sm:flex-col gap-2">
                <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val); setPage(1); }}>
                  <SelectTrigger className="w-40 focus-gold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover open={organizerOpenDesktop} onOpenChange={setOrganizerOpenDesktop}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={organizerOpenDesktop}
                      className="flex flex-wrap focus-gold justify-between font-normal"
                      disabled={loadingOrganizers}
                    >
                      <div className="flex items-center justify-between w-full">
                        {loadingOrganizers ? (
                          <span className="truncate max-w-[200px] text-gray-400">Loading...</span>
                        ) : filterOrganizer === 'all' ? (
                          <span className="truncate max-w-[200px]">All Organizers</span>
                        ) : (
                          (() => {
                            const name = organizers.find(o => o.id.toString() === filterOrganizer)?.name || 'All Organizers';
                            return (
                              <ResponsiveTooltip content={<p className="text-sm whitespace-pre-wrap">{name}</p>}>
                                <span className="truncate max-w-[120px]">{name}</span>
                              </ResponsiveTooltip>
                            );
                          })()
                        )}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40 max-h-40 p-0" align="start">
                    <Command className="border border-gray-200">
                      <CommandInput placeholder="Search" />
                      <CommandList>
                        <CommandEmpty>No organizer found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setFilterOrganizer('all');
                              setPage(1);
                              setOrganizerOpenDesktop(false);
                            }}
                            className="hover:bg-gray-100 "
                          >
                            All Organizers
                          </CommandItem>
                          {organizers.map((organizer) => (
                            <CommandItem
                              key={organizer.id}
                              value={`${organizer.name} ${organizer.id}`}
                              onSelect={() => {
                                setFilterOrganizer(organizer.id.toString());
                                setPage(1);
                                setOrganizerOpenDesktop(false);
                              }}
                            >
                              {organizer.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>


              </div>
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <Search className="absolute  left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search  "
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 focus-gold"
                  />
                </div>
                <div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="w-full flex py-2 justify-center items-center gap-2 cursor-pointer text-xs rounded-lg transition-all hover:opacity-90 active:scale-[0.98]"
                        style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--hover-gold))', color: 'white' }}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Export Meeting
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[80vw]  sm:w-72 p-0 overflow-hidden shadow-2xl border border-gray-100 z-50"
                      align="end"
                      side="bottom"
                      sideOffset={8}
                    >
                      <div className="p-3 sm:p-4 space-y-2">
                        <h4 className="font-semibold text-sm sm:text-base text-blue-900">Export Date Range</h4>
                        <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight">Select the period you want to export meetings for.</p>
                        <p className="text-[9px] sm:text-[10px] text-red-500 font-medium italic">* You can only get the data of max 90days</p>

                        <div className="flex justify-center pt-1">
                          <Calendar
                            mode="range"
                            defaultMonth={exportDateRange?.from}
                            selected={exportDateRange}
                            onSelect={setExportDateRange}
                            numberOfMonths={1}
                            className="rounded-md border p-0.5 sm:p-1"
                          />
                        </div>
                      </div>

                      <div className="bg-blue-50/80 p-3 mt-auto flex justify-between items-center border-t border-blue-100">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-blue-600 uppercase font-bold tracking-wider">Selection</span>
                          <span className="text-xs font-bold text-blue-900">
                            {exportDateRange?.from ? (
                              exportDateRange.to ? (
                                `${format(exportDateRange.from, "MMM dd")} - ${format(exportDateRange.to, "MMM dd")}`
                              ) : (
                                format(exportDateRange.from, "MMM dd, yyyy")
                              )
                            ) : (
                              "Entire History"
                            )}
                          </span>
                        </div>
                        <button
                          onClick={handleExport}
                          className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm flex gap-2 px-3 py-2 cursor-pointer text-[11px] font-bold rounded-md items-center transition-all"
                          style={{ background: '#f59e0b', cursor: loadingExport ? 'not-allowed' : 'pointer' }}
                          disabled={loadingExport}
                        >
                          {loadingExport ? (
                            <Loader className="w-3.5 h-3.5" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          Download XLSX
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Action Buttons */}


        {/* Filters */}


        {/* Meetings Display */}
        {
          loadingMeetings ? (
            <div className="space-y-3">
              <div className="loading-shimmer h-12 w-full rounded-lg"></div>
              <div className="loading-shimmer h-64 w-full rounded-lg"></div>
            </div>
          ) : totalFiltered === 0 ? (
            <div className="text-center py-8  kspl">
              <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {debouncedSearch || filterStatus !== 'all' || filterOrganizer !== 'all'
                  ? 'No meetings match your search criteria'
                  : 'No meetings found'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {(debouncedSearch || filterStatus !== 'all' || filterOrganizer !== 'all') && 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block rounded-lg border overflow-x-auto">
                <div className="max-h-[calc(100vh-300px)] overflow-auto">
                  <div className="relative w-full overflow-visible">
                    <table className="w-full overflow-x-auto caption-bottom text-sm">
                      <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                        <tr className='bg-gray-100 border-b'>
                          <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">Sr.<br></br>No.</th>
                          <th
                            className="h-10  text-left align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('title')}
                          >
                            <div className="flex items-center justify-start px-2">
                              Meeting
                              {getSortIcon('title')}
                            </div>
                          </th>


                          <th
                            className="h-10 px-2 text-center align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => handleSort('date')}
                          >
                            <div className="flex items-center justify-center">
                              Date
                              {getSortIcon('date')}
                            </div>
                          </th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Time</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Organizer</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Participants</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Action Points</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Location</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Meeting Type</th>
                          <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="[&_tr:last-child]:border-0 ">
                        {paginatedMeetings.map((meeting, index) => {
                          const actionPointsCount = meeting.action_points_total || 0;
                          const completedActionPoints = meeting.action_points_completed || 0;

                          return (
                            <tr key={meeting.id} className="border-b transition-colors hover:bg-gray-50">
                              <td className="p-2 align-middle">
                                {(page - 1) * pageSize + index + 1}
                              </td>
                              <td className="p-2 align-middle text-left">
                                {meeting.title && meeting.title.length > 20 ? (
                                  <ResponsiveTooltip
                                    content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                    className="max-w-md p-3"
                                  >
                                    <p className="font-medium text-gray-900 cursor-pointer">
                                      {truncateText(meeting.title, 20)}
                                    </p>
                                  </ResponsiveTooltip>
                                ) : (
                                  <p className="font-medium text-gray-900">{meeting.title || '-'}</p>
                                )}
                              </td>


                              <td className="p-2 align-middle text-center">
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}</p>

                                </div>
                              </td>
                              <td className="p-2 align-middle text-center text-nowrap">
                                <span className="text-sm font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                              </td>
                              <td className="p-2 align-middle flex items-center justify-center">
                                <span className="text-sm font-medium">{meeting.organizer?.full_name}</span>
                              </td>
                              <td className="p-2 align-middle">
                                <ResponsiveTooltip
                                  content={
                                    <div className="space-y-1 max-h-40 w-40 overflow-y-auto">
                                      {meeting.participants && meeting.participants.length > 0 && (
                                        <div className="space-y-0.5">
                                          <div className="text-xs font-semibold">Participants:</div>
                                          {meeting.participants.map((participant, idx) => (
                                            <div key={idx} className="text-xs">
                                              {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  }
                                >
                                  <div className="flex items-center justify-center gap-1 cursor-pointer">
                                    <Users className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm">{meeting.participant_count}</span>
                                  </div>
                                </ResponsiveTooltip>
                              </td>
                              <td className="p-2 align-middle text-center">
                                <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                  {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                </Badge>
                              </td>
                              <td className="p-2 align-middle">
                                <div className="flex items-center gap-2 justify-center">
                                  {actionPointsCount > 0 ? (
                                    <ResponsiveTooltip content={<p className="text-xs">View action points ({actionPointsCount})</p>}>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleActionPointsViewModalOpen(meeting)}
                                        className="h-8 w-8 p-0 hover:bg-blue-50"
                                      >
                                        <Eye className="w-4 h-4 text-blue-600" />
                                      </Button>
                                    </ResponsiveTooltip>
                                  ) : (
                                    <span className="text-xs text-gray-400">None</span>
                                  )}
                                  {(meeting.state === 'ongoing' || meeting.state === 'pending' || meeting.state === 'completed') && currentUser?.role === 'admin' && meeting.can_add_mom && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleMOMModalOpen(meeting)}
                                      className="text-xs h-6 px-2"
                                    >
                                      <FileText className="w-3 h-3 mr-1" />
                                      MOM
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td className='p-2'>
                                <div className="flex items-center justify-center gap-1">
                                  {meeting.location && meeting.location.length > 20 ? (
                                    <ResponsiveTooltip
                                      content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                      className="max-w-xs p-3"
                                    >
                                      <span className="text-sm cursor-pointer">
                                        {truncateText(meeting.location, 20)}
                                      </span>
                                    </ResponsiveTooltip>
                                  ) : (
                                    <span className="text-sm">{meeting.location || '-'}</span>
                                  )}
                                </div>
                              </td>
                              <td className='p-2 text-center'>
                                <Badge variant="outline" className="text-[11px] px-2 py-0 capitalize">
                                  {meeting.meeting_type || '-'}
                                </Badge>
                              </td>
                              <td className="p-2 align-middle">
                                <div className="flex justify-center gap-2">
                                  {/* Edit Meeting */}
                                  <ResponsiveTooltip content={<p className="text-xs">Edit Meeting</p>}>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedMeeting(meeting);
                                        setIsEditMeetingModalOpen(true);
                                      }}
                                      className="h-8 w-8 p-0 hover:bg-blue-50"
                                      disabled={!meeting.can_edit || meeting.state === 'completed' || meeting.state === 'cancelled'}
                                    >
                                      <Edit className={`w-4 h-4 ${!meeting.can_edit || meeting.state === 'completed' || meeting.state === 'cancelled' ? 'text-gray-400' : 'text-blue-600'}`} />
                                    </Button>
                                  </ResponsiveTooltip>

                                  {/* Cancel Meeting (admin or organizer can cancel) */}
                                  {/* For recurring: always show (except cancelled) to allow canceling future occurrences */}
                                  {/* For non-recurring: only show for upcoming */}
                                  {/* Cancel button - use has_future_cancellable_instances from backend */}
                                  {((meeting.recurring_series_id || meeting.is_recurring || (meeting.recurrence && meeting.recurrence !== 'none'))
                                    ? (meeting.state !== 'cancelled' && (meeting.has_future_cancellable_instances !== false || meeting.state === 'upcoming'))  // Recurring: show only if not cancelled AND has future instances
                                    : meeting.state === 'upcoming'   // Non-recurring: only upcoming
                                  ) &&
                                    (currentUser?.role === 'admin' || meeting.organizer?.id === currentUser?.id) && (
                                      <ResponsiveTooltip content={<p className="text-xs">
                                        {meeting.state === 'completed' || meeting.state === 'ongoing' || meeting.state === 'pending'
                                          ? 'Cancel Future Occurrences'
                                          : 'Cancel Meeting'}
                                      </p>}>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleCancelClick(meeting)}
                                          className="border-none shadow-none hover:bg-red-50"
                                        >
                                          <Trash className="w-4 h-4 text-red-600" />
                                        </Button>
                                      </ResponsiveTooltip>
                                    )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination Controls - Desktop */}
              <div className="hidden md:block">
                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  pageSize={pageSize}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  onPageChange={(p) => setPage(Math.max(1, p))}
                  onPageSizeChange={(s) => { setPageSize(Number(s)); setPage(1); }}
                />
              </div>

              {/* Mobile Accordion View */}
              <div className="md:hidden">
                <Accordion type="multiple" className="w-full">
                  {paginatedMeetings.map(meeting => {
                    const actionPointsCount = meeting.action_points_total || 0;
                    const completedActionPoints = meeting.action_points_completed || 0;

                    return (
                      <AccordionItem key={meeting.id} value={`meeting-${meeting.id}`} className="border border-gray-200 rounded-lg mb-2">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center space-x-3 flex-1 text-left">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                                <CalendarIcon className="w-5 h-5 text-yellow-600" />
                              </div>
                            </div>
                            <div className="flex-1  min-w-0">
                              <div className="flex flex-col">
                                {meeting.title && meeting.title.length > 20 ? (
                                  <ResponsiveTooltip
                                    content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                    className="max-w-md p-3"
                                  >
                                    <p className="font-medium text-gray-900 text-wrap cursor-pointer">
                                      {truncateText(meeting.title, 20)}
                                    </p>
                                  </ResponsiveTooltip>
                                ) : (
                                  <p className="font-medium text-gray-900 text-wrap">{meeting.title || '-'}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                    {meeting.state}
                                  </Badge>
                                  <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                    {meeting.meeting_type || '-'}
                                  </Badge>
                                </div>
                              </div>

                              <div className="flex flex-wrap  mt-1 items-center  gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd') : '-'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  <span>{meeting.participant_count}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {meeting.location && meeting.location.length > 20 ? (
                                    <ResponsiveTooltip
                                      content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                      className="max-w-md p-3"
                                    >
                                      <span className="truncate max-w-[80px] cursor-pointer">
                                        {truncateText(meeting.location, 20)}
                                      </span>
                                    </ResponsiveTooltip>
                                  ) : (
                                    <span className="truncate max-w-[80px]">{meeting.location || '-'}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-4">
                            {/* Date & Time */}
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'EEEE, MMMM dd, yyyy') : '-'}</p>
                                <p className="text-xs text-gray-500">
                                  {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                                </p>
                              </div>
                            </div>

                            {/* Meeting Type */}
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                {meeting.meeting_type || '-'}
                              </Badge>
                            </div>

                            {/* Organizer */}
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium">Organizer</p>
                                <p className="text-xs text-gray-600">{meeting.organizer?.full_name}</p>
                              </div>
                            </div>

                            {/* Participants */}
                            {meeting.participants && meeting.participants.length > 0 && (
                              <div className="flex items-start gap-2">
                                <Users className="w-4 h-4 text-gray-400 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium mb-1">Participants ({meeting.participant_count})</p>
                                  <div className="flex flex-wrap gap-1">
                                    <ResponsiveTooltip content={<p>{meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}</p>}>
                                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded cursor-pointer">
                                        {meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}
                                      </span>
                                    </ResponsiveTooltip>
                                    {meeting.participants.map((participant, index) => {
                                      const participantName = participant?.full_name || participant?.name || participant?.email || 'Unknown';
                                      return (
                                        <ResponsiveTooltip key={index} content={<p>{participantName}</p>}>
                                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded cursor-pointer">
                                            {participantName}
                                          </span>
                                        </ResponsiveTooltip>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Meeting Link */}
                            {meeting.join_url && (
                              <div className="flex items-center gap-2">
                                <Link className="w-4 h-4 text-gray-400" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">Meeting Link</p>
                                  <a
                                    href={meeting.join_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline break-all"
                                  >
                                    {meeting.join_url}
                                  </a>
                                </div>
                              </div>
                            )}

                            {/* Agenda */}
                            {meeting.agenda && (
                              <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium mb-1">Agenda</p>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <div
                                        className="text-xs text-gray-600 cursor-pointer text-left overflow-hidden rich-text-content"
                                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(meeting.agenda) }}
                                      />
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72 p-3 max-h-64 overflow-y-auto overflow-x-hidden break-words" align="start">
                                      <div
                                        className="text-sm text-gray-900 rich-text-content max-w-none"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(meeting.agenda) }}
                                      />
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </div>
                            )}

                            {/* Action Points */}
                            <div className="flex items-start gap-2">
                              <CheckSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-medium">Action Points</p>
                                  {(meeting.state === 'ongoing' || meeting.state === 'pending' || meeting.state === 'completed') && currentUser?.role === 'admin' && meeting.can_add_mom && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleMOMModalOpen(meeting)}
                                      className="text-xs h-6 px-2"
                                    >
                                      <FileText className="w-3 h-3 mr-1" />
                                      MOM
                                    </Button>
                                  )}
                                </div>
                                {actionPointsCount > 0 ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleActionPointsViewModalOpen(meeting)}
                                    className="h-8 w-8 p-0 hover:bg-blue-50"
                                  >
                                    <Eye className="w-4 h-4 text-blue-600" />
                                  </Button>
                                ) : (
                                  <span className="text-xs text-gray-400">None</span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-2 border-t">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedMeeting(meeting);
                                  setIsEditMeetingModalOpen(true);
                                }}
                                className="flex-1"
                                disabled={!meeting.can_edit || meeting.state === 'completed' || meeting.state === 'cancelled'}
                              >
                                <Edit className={`w-4 h-4 mr-2 ${!meeting.can_edit || meeting.state === 'completed' || meeting.state === 'cancelled' ? 'text-gray-400' : ''}`} />
                                Edit
                              </Button>

                              {/* Cancel Meeting (admin or organizer can cancel) */}
                              {(meeting.state === 'upcoming' ||
                                ((meeting.state === 'ongoing' || meeting.state === 'completed') &&
                                  (meeting.is_recurring || (meeting.recurrence && meeting.recurrence !== 'none')))) &&
                                (currentUser?.role === 'admin' || meeting.organizer?.id === currentUser?.id) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelClick(meeting)}
                                    className="flex-1 text-red-600 hover:text-red-700"
                                  >
                                    <Trash className="w-4 h-4 mr-2" />
                                    Cancel
                                  </Button>
                                )}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>

              {/* Pagination Controls - Mobile */}
              <div className="md:hidden">
                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  pageSize={pageSize}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  onPageChange={(p) => setPage(Math.max(1, p))}
                  onPageSizeChange={(s) => { setPageSize(Number(s)); setPage(1); }}
                />
              </div>
            </>
          )
        }
      </CardContent >



      {/* Modals */}
      < CreateMeetingModal
        open={isCreateMeetingModalOpen}
        onClose={() => setIsCreateMeetingModalOpen(false)}
        onSubmit={handleCreateMeeting}
        users={allParticipants}
      />

      <EditMeetingModal
        open={isEditMeetingModalOpen}
        onClose={() => setIsEditMeetingModalOpen(false)}
        meeting={selectedMeeting}
        onSubmit={handleUpdateMeeting}
        users={allParticipants}
      />

      <MOMModal
        open={isMOMModalOpen}
        onClose={() => setIsMOMModalOpen(false)}
        meeting={selectedMeeting}
        users={allUsers}
        onSuccess={handleMOMModalSuccess}
      />

      <ActionPointsViewModal
        open={isActionPointsViewModalOpen}
        onClose={() => setIsActionPointsViewModalOpen(false)}
        meeting={selectedMeeting}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Meeting
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete this meeting? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {meetingToDelete && (
            <div className="py-4 space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h4 className="font-medium text-gray-900">{meetingToDelete.title}</h4>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {meetingToDelete.scheduled_date ? format(new Date(meetingToDelete.scheduled_date), 'MMM dd, yyyy') : '-'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(meetingToDelete.start_time)} - {formatTime(meetingToDelete.end_time)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getStatusBadgeColor(meetingToDelete.state)} text-xs`}>
                        {meetingToDelete.state}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {meetingToDelete.participant_count} participants
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  This will permanently delete the meeting and all associated data, including action points and participant records.
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setMeetingToDelete(null);
              }}
            >
              Cancel
            </Button>

          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Trash className="w-5 h-5" />
              Cancel Meeting
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to cancel this meeting? Participants will be notified.
            </DialogDescription>
          </DialogHeader>
          {meetingToCancel && (
            <div className="py-4 space-y-3">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <h4 className="font-medium text-gray-900">{meetingToCancel.title}</h4>
                    <div className="flex flex-col gap-1 text-sm text-gray-600">
                      <span className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5" />
                        {meetingToCancel.scheduled_date ? format(new Date(meetingToCancel.scheduled_date), 'EEEE, MMM dd, yyyy') : '-'}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTime(meetingToCancel.start_time)} - {formatTime(meetingToCancel.end_time)}
                      </span>
                      <span className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" />
                        {meetingToCancel.participant_count} Participant{meetingToCancel.participant_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This will cancel the meeting and notify all participants. Any pending action points will remain in their current state.
                  </span>
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setCancelConfirmOpen(false);
                setMeetingToCancel(null);
              }}
            >
              Keep Meeting
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancelMeeting}
              className="gap-2 bg-orange-600 hover:bg-orange-700"
            >
              <Trash className="w-4 h-4" />
              Cancel Meeting
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recurring Meeting Cancel Dialog */}
      <CancelRecurringMeetingDialog
        open={isRecurringCancelDialogOpen}
        onClose={() => {
          setIsRecurringCancelDialogOpen(false);
          setMeetingToCancel(null);
        }}
        meeting={meetingToCancel}
        onConfirm={handleRecurringCancelConfirm}
        isLoading={isCancelLoading}
      />
    </Card >
  );
};

export default MeetingManagement;